import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import ImageCarousel from './ImageCarousel';
import { useTranslation } from '../contexts/TranslationContext';
import { getLocalizedString, type LocalizedString } from '../utils/localizedText';

export type PageMediaTextSectionConfig = {
  enabled?: boolean;
  title?: LocalizedString;
  text?: LocalizedString;
  images?: string[];
  mediaSide?: 'left' | 'right';
  imageHeight?: number;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showArrows?: boolean;
  showDots?: boolean;
  eyebrow?: LocalizedString;
  ctaText?: LocalizedString;
  ctaUrl?: string;
};

type Props = {
  config?: PageMediaTextSectionConfig | null;
  resolvePublicUrl?: (p?: string) => string;
};

const clamp = (n: any, min: number, max: number) => Math.max(min, Math.min(max, Number(n) || 0));

const PageMediaTextSection: React.FC<Props> = ({ config, resolvePublicUrl }) => {
  const cfg: PageMediaTextSectionConfig = (config && typeof config === 'object') ? config : {};

  const { language } = useTranslation();

  const title = getLocalizedString(cfg.title, language, 'de', 'Unsere Story');
  const text = getLocalizedString(cfg.text, language, 'de', 'Erzähle hier deine Geschichte, deine Mission oder warum Kunden dir vertrauen sollten. Kurz, klar, überzeugend.');
  const eyebrow = getLocalizedString(cfg.eyebrow, language, 'de', '');
  const mediaSide: 'left' | 'right' = cfg.mediaSide === 'right' ? 'right' : 'left';
  const imageHeight = clamp(cfg.imageHeight, 220, 640) || 420;
  const autoPlay = cfg.autoPlay === true;
  const autoPlayInterval = clamp(cfg.autoPlayInterval, 1500, 20000) || 4500;
  const showArrows = cfg.showArrows !== false;
  const showDots = cfg.showDots !== false;
  const ctaText = getLocalizedString(cfg.ctaText, language, 'de', '');
  const ctaUrl = typeof cfg.ctaUrl === 'string' ? cfg.ctaUrl : '';

  const resolve = React.useCallback((p?: string) => {
    if (!p) return '';
    return resolvePublicUrl ? resolvePublicUrl(p) : p;
  }, [resolvePublicUrl]);

  const images = Array.isArray(cfg.images)
    ? cfg.images.filter((s: any) => typeof s === 'string' && s).map((s: string) => resolve(s))
    : [];

  if (cfg.enabled === false) return null;

  const media = (
    <Box
      sx={{
        borderRadius: { xs: 4, md: 5 },
        overflow: 'hidden',
        boxShadow: '0 18px 50px rgba(2,6,23,0.14)',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {images.length > 0 ? (
        <ImageCarousel
          images={images}
          autoPlay={autoPlay}
          autoPlayInterval={autoPlayInterval}
          showControls={showArrows}
          showDots={showDots}
          height={imageHeight}
          borderRadius={0}
          boxShadow={0}
        />
      ) : (
        <Box sx={{ height: imageHeight, display: 'grid', placeItems: 'center', bgcolor: 'grey.100' }}>
          <Typography variant="body2" color="text.secondary">Keine Bilder ausgewählt.</Typography>
        </Box>
      )}
    </Box>
  );

  const content = (
    <Box sx={{ maxWidth: 640, mx: mediaSide === 'right' ? 0 : { xs: 'auto', md: 0 } }}>
      {eyebrow ? (
        <Typography
          variant="overline"
          sx={{
            fontWeight: 900,
            letterSpacing: 1.2,
            color: 'primary.main',
          }}
        >
          {eyebrow}
        </Typography>
      ) : null}
      <Typography variant="h4" sx={{ mt: eyebrow ? 0.5 : 0, fontWeight: 950, letterSpacing: -0.4, lineHeight: 1.1 }}>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.85 }}>
        {text}
      </Typography>
      {ctaText && ctaUrl ? (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            href={ctaUrl}
            sx={{
              borderRadius: 999,
              px: 3,
              py: 1.25,
              fontWeight: 900,
              textTransform: 'none',
              boxShadow: '0 14px 34px rgba(59,130,246,0.25)',
            }}
          >
            {ctaText}
          </Button>
        </Box>
      ) : null}
    </Box>
  );

  return (
    <Box sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
            gap: { xs: 4, md: 6 },
            alignItems: 'center',
          }}
        >
          {mediaSide === 'left' ? (
            <>
              {media}
              {content}
            </>
          ) : (
            <>
              {content}
              {media}
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PageMediaTextSection;
